import { PageShell } from '@/components/layout/PageShell'
import { SectionList, sectionQueries } from '@/components/blocks/BlockRenderer'
import { HeroSection } from '@/components/sections/HeroSection'
import { MaterialCard } from '@/components/app/MaterialCard'
import { EventCard } from '@/components/app/EventCard'
import { LocaleLink as Link } from '@/components/LocaleLink'
import { useFeaturedMaterials, useLatestNews, usePage, useUpcomingEvents } from '@/lib/cms/hooks'
import type { Route } from './+types/HomePage'
import { createContentLoader } from '@/lib/cms/prefetch.server'
import { contentQueries } from '@/lib/cms/queries'
import { HOME_PAGE_SLUG } from '@/lib/cmsPages'
import { formatDate } from '@/lib/formatDate'
import { useTranslation } from '@/lib/i18n/LocaleContext'
import { localeFromParams } from '@/lib/routeLocale'
import { buildMeta, type PageSeo } from '@/lib/seo'

export async function loader({ params }: Route.LoaderArgs) {
  const content = createContentLoader(localeFromParams(params))
  // The front page must render even with an empty or unreachable CMS: a
  // failure here falls back to the built-in hero instead of an error page.
  const page = await content.fetch(contentQueries.page(HOME_PAGE_SLUG)).catch(() => null)
  await content.prefetch([
    ...(page?.sections.length ? sectionQueries(page.sections) : []),
    contentQueries.featuredMaterials(),
    contentQueries.upcomingEvents(),
    contentQueries.latestNews(),
  ])
  const seo: PageSeo = { title: page?.seoTitle, description: page?.seoDescription }
  return { dehydratedState: content.dehydrate(), seo }
}

export const meta: Route.MetaFunction = (args) => buildMeta(args, args.data?.seo)

export default function HomePage() {
  const { t, locale } = useTranslation()
  const { data, loading, error } = usePage(HOME_PAGE_SLUG)
  const useFallback = loading || !!error || !data || data.sections.length === 0
  const { data: featured } = useFeaturedMaterials()
  const { data: events } = useUpcomingEvents()
  const { data: news } = useLatestNews()
  const next = events?.[0]

  return (
    <PageShell>
      {useFallback ? <HeroSection /> : <SectionList sections={data.sections} />}

      <div className="wrap home-grid">
        {featured && featured.length > 0 && (
          <section>
            <h2 className="h3">{t.app.featured}</h2>
            <ul className="card-list">
              {featured.map((m) => <li key={m.id}><MaterialCard material={m} /></li>)}
            </ul>
            <p className="small"><Link to="/materialer" className="more-link">{t.app.seeAll} →</Link></p>
          </section>
        )}

        {next && (
          <section>
            <h2 className="h3">{t.app.nextEvent}</h2>
            <EventCard event={next} />
          </section>
        )}

        {news && news.length > 0 && (
          <section>
            <h2 className="h3">{t.app.news}</h2>
            <ul className="news-list">
              {news.map((n) => (
                <li key={n.id}>
                  <span className="muted small">{formatDate(n.publishedAt, locale)}</span>
                  <strong>{n.title}</strong>
                  {n.excerpt && <span className="small">{n.excerpt}</span>}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </PageShell>
  )
}
